import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import {
  CheckCircle,
  Clock,
  RefreshCw,
  Sparkles,
  UserPlus,
} from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { preLoadDefaultProject, projectQueries } from '@/lib/queries/project';

import { IntegrationsPrompt } from './-components/IntegrationsPrompt';

export const Route = createFileRoute('/_root/getting-started/slack-channels')({
  component: RouteComponent,
  beforeLoad: ({ context: { formatMessage } }) => ({
    title: formatMessage({
      id: 'global.breadcrumb.gettingStartedSlackChannels',
    }),
  }),
  loader: async ({ context }) => {
    const project = await preLoadDefaultProject({ context });

    if (project.slackWorkspaceName === undefined) {
      throw redirect({
        to: '/getting-started',
      });
    }

    return project;
  },
});

function useAnnieStatus() {
  const defaultProject = Route.useLoaderData();
  const { data, isLoading, isFetching, refetch } = useQuery({
    ...projectQueries.default(),
    refetchInterval: 10_000,
    refetchIntervalInBackground: true,
    staleTime: 0, // Always consider data stale to ensure fresh fetches
  });

  const annieOnCall =
    data?.defaultProject?.installedOnChannel ??
    defaultProject.installedOnChannel ??
    false;

  return { annieOnCall, isLoading, isFetching, refetch };
}

function useOnboardingSteps() {
  const { formatMessage } = useIntl();

  return [
    {
      icon: UserPlus,
      title: formatMessage({
        id: 'gettingStarted.slackChannels.steps.addAnnie.title',
      }),
      description: formatMessage({
        id: 'gettingStarted.slackChannels.steps.addAnnie.description',
      }),
    },
    {
      icon: Sparkles,
      title: formatMessage({
        id: 'gettingStarted.slackChannels.steps.greetAnnie.title',
      }),
      description: formatMessage({
        id: 'gettingStarted.slackChannels.steps.greetAnnie.description',
      }),
    },
  ];
}

function RouteComponent() {
  const { annieOnCall, isLoading, isFetching, refetch } = useAnnieStatus();
  const steps = useOnboardingSteps();

  return (
    <div className="container mx-auto mt-4">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-transparent">
            <FormattedMessage id="gettingStarted.slackChannels.title" />
          </h1>
        </div>

        <div className="space-y-8">
          {steps.map((step, index) => (
            <Card key={index} className="border-l-4 border-l-blue-500">
              <CardHeader>
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                    <step.icon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">
                      Step {index + 1}: {step.title}
                    </CardTitle>
                    <CardDescription className="text-base mt-1">
                      {step.description}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
            </Card>
          ))}
        </div>

        <div className="mt-12 text-center">
          {annieOnCall ? (
            <Card className="bg-gradient-to-br from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 border-green-200 dark:border-green-700">
              <CardContent className="p-8">
                <div className="flex items-center justify-center mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  <FormattedMessage id="gettingStarted.slackChannels.success.title" />
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  <FormattedMessage id="gettingStarted.slackChannels.success.description1" />
                </p>
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  <FormattedMessage id="gettingStarted.slackChannels.success.description2" />{' '}
                  <code>
                    <FormattedMessage id="gettingStarted.slackChannels.success.command" />
                  </code>{' '}
                  <FormattedMessage id="gettingStarted.slackChannels.success.commandText" />
                </p>

                <Link to="/">
                  <Button
                    size="lg"
                    className="bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all"
                  >
                    <FormattedMessage id="gettingStarted.slackChannels.success.buttonText" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-amber-200 dark:border-amber-700">
                <CardContent className="p-8">
                  <div className="flex items-center justify-center mb-4">
                    <Clock className="w-8 h-8 text-amber-600 dark:text-amber-400 animate-pulse" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    <FormattedMessage id="gettingStarted.slackChannels.waiting.title" />
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-6">
                    <FormattedMessage id="gettingStarted.slackChannels.waiting.description" />
                  </p>
                  <div className="flex justify-center">
                    <Button
                      size="lg"
                      onClick={() => refetch()}
                      disabled={isLoading || isFetching}
                      className="bg-amber-600 hover:bg-amber-700 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all"
                    >
                      <RefreshCw
                        className={`w-5 h-5 mr-2 ${isLoading || isFetching ? 'animate-spin' : ''}`}
                      />
                      <FormattedMessage id="gettingStarted.slackChannels.waiting.checkNow" />
                    </Button>
                  </div>
                  <p className="text-xs text-amber-600 dark:text-amber-400 text-center mt-2">
                    <FormattedMessage id="gettingStarted.slackChannels.waiting.note" />
                  </p>
                </CardContent>
              </Card>

              <IntegrationsPrompt />
            </div>
          )}
        </div>

        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            <FormattedMessage id="gettingStarted.slackChannels.support.text" />{' '}
            <a href="mailto:support@anyshift.io">
              <FormattedMessage id="gettingStarted.slackChannels.support.email" />
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
